import { mkdir, writeFile } from "node:fs/promises"
import path from "node:path"

import { AnalyzerArtifacts, UsageRecord } from "../analyzers/types.js"

type Input = {
  artifacts: AnalyzerArtifacts
  rawArtifacts?: RawArtifactName[]
  snapshotRoot: string
  usageImportFromPackages?: string[]
}

type RawArtifactName =
  | "static-components"
  | "typescript-api"
  | "storybook-stories"
  | "storybook-docs"
  | "jsdoc"
  | "component-usage"

export type ComponentUsageSnapshot = {
  meta: {
    importFromPackages: string[]
  }
  packages: Record<
    string,
    {
      records: Record<string, UsageRecord>
    }
  >
}

async function ensureSnapshotLayout(snapshotRoot: string): Promise<void> {
  await mkdir(path.join(snapshotRoot, "raw"), { recursive: true })
}

function isEmptyJsonPayload(payload: unknown): boolean {
  if (payload === null || payload === undefined) {
    return true
  }

  if (Array.isArray(payload)) {
    return payload.length === 0
  }

  if (typeof payload === "object") {
    return Object.keys(payload as Record<string, unknown>).length === 0
  }

  return false
}

async function writeJson(filePath: string, payload: unknown): Promise<void> {
  if (isEmptyJsonPayload(payload)) {
    return
  }

  await writeFile(filePath, `${JSON.stringify(payload, null, 2)}\n`, "utf8")
}

export async function writeSnapshot(input: Input): Promise<void> {
  const { artifacts, snapshotRoot } = input
  await ensureSnapshotLayout(snapshotRoot)

  const rawArtifacts = new Set<RawArtifactName>(
    input.rawArtifacts ?? ["static-components", "typescript-api", "storybook-stories", "storybook-docs", "jsdoc", "component-usage"],
  )

  if (rawArtifacts.has("static-components")) {
    await writeJson(path.join(snapshotRoot, "raw", "static-components.json"), artifacts.staticComponents)
  }

  if (rawArtifacts.has("typescript-api")) {
    await writeJson(path.join(snapshotRoot, "raw", "typescript-api.json"), artifacts.typescriptApi)
  }

  if (rawArtifacts.has("storybook-stories")) {
    await writeJson(path.join(snapshotRoot, "raw", "storybook-stories.json"), artifacts.storybookStories)
  }

  if (rawArtifacts.has("storybook-docs")) {
    await writeJson(path.join(snapshotRoot, "raw", "storybook-docs.json"), artifacts.storybookDocs)
  }

  if (rawArtifacts.has("jsdoc")) {
    await writeJson(path.join(snapshotRoot, "raw", "jsdoc.json"), artifacts.jsdoc)
  }

  if (rawArtifacts.has("component-usage")) {
    const usageRecords = artifacts.componentUsage
    if (isEmptyJsonPayload(usageRecords)) {
      return
    }

    const importFromPackages = [...new Set((input.usageImportFromPackages ?? []).map(item => item.trim()).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b),
    )
    const packageNames = [...new Set([...importFromPackages, ...Object.keys(usageRecords)])]

    const payload: ComponentUsageSnapshot = {
      meta: {
        importFromPackages,
      },
      packages: Object.fromEntries(
        packageNames.map(packageName => [packageName, { records: usageRecords[packageName] ?? {} }]),
      ),
    }

    await writeJson(path.join(snapshotRoot, "raw", "component-usage.json"), payload)
  }
}
